import { Maybe, rd, RemoteData } from '@passionware/monads';
import { AppScopeIdentity } from 'v5/core/app-scope/appScope.types';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import { FeatureFlagValues } from 'v5/platform/feature-management/feature-management';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureService } from '../FeatureService/FeatureService';
import {
  Permission,
  WithPermissionService
} from '../PermissionService/PermissionService';
import {
  AccessQuery,
  AccessService,
  actionToFeatureMap,
  featureFlaggedPemissions,
  SpecialCapability,
  specialCapabilities
} from './AccessService';

const findFeatureForAction = (action: Permission) =>
  actionToFeatureMap.get(action) ?? null;

const validateFeatureQuery = (feature: string) => {
  if (featureFlaggedPemissions[feature]) {
    throw new Error(
      `Feature '${feature}' requires specific permissions. Use action query instead of feature query.`
    );
  }
};

const isSpecialCapability = (action: Permission | SpecialCapability) =>
  specialCapabilities.includes(action as SpecialCapability);

/**
 * Handle special capability cases that are not in CapabilityEnum
 */
const checkSpecialCapability = (
  action: SpecialCapability,
  getIsSuperUser: () => boolean,
  permissionChecker: (permission: Permission) => boolean
): boolean => {
  // For now, only ManageFeatureFlags is handled
  // In the future, add more cases here if needed
  switch (action) {
    case 'manage_feature_flags':
      return (
        getIsSuperUser() || permissionChecker(Permission.MANAGE_FEATURE_FLAGS)
      );
    default:
      return false;
  }
};

/**
 * Core access control logic that can be used with both reactive and async data sources.
 * Extracted from useCan and can to eliminate duplication.
 */
const checkAccess = (
  accessQuery: AccessQuery,
  permissionChecker: (action: Permission) => boolean,
  featureFlags: FeatureFlagValues<AtellioFeatureGroupDefinition>,
  getIsSuperUser: () => boolean
) => {
  if ('feature' in accessQuery) {
    // It's a feature query - validate first, then check feature flag
    validateFeatureQuery(accessQuery.feature);
    // Feature doesn't require specific permissions, just feature flag
    return !!featureFlags[accessQuery.feature];
  } else if ('action' in accessQuery) {
    // Check if it's a special capability (not in CapabilityEnum)
    if (isSpecialCapability(accessQuery.action)) {
      return checkSpecialCapability(
        accessQuery.action as SpecialCapability,
        getIsSuperUser,
        permissionChecker
      );
    }
    // It's an action query - check permission first, then feature flag if tied
    if (!permissionChecker(accessQuery.action as Permission)) {
      return false;
    }
    const relatedFeature = findFeatureForAction(
      accessQuery.action as Permission
    );
    if (relatedFeature) {
      // Action is tied to a feature - check feature flag
      return !!featureFlags[relatedFeature];
    }
    // Action not tied to any feature, permission check is sufficient
    return true;
  }
  return false;
};

function bindCan(
  featureFlags: RemoteData<FeatureFlagValues<AtellioFeatureGroupDefinition>>,
  hasPermission: (action: Permission) => boolean,
  getIsSuperUser: () => boolean
): (accessQuery: AccessQuery) => RemoteData<boolean> {
  return accessQuery =>
    rd.map(featureFlags, flags =>
      checkAccess(accessQuery, hasPermission, flags, getIsSuperUser)
    );
}

export function createAccessService(
  config: WithServices<[WithFeatureService, WithPermissionService]> & {
    getIsSuperUser: () => boolean;
  }
): AccessService {
  const { featureService, permissionService } = config.services;

  return {
    useCan: (scope: Maybe<AppScopeIdentity>) => {
      const hasPermission = permissionService.useHasPermission();
      const featureFlags = featureService.useFeatureFlags(scope);

      return bindCan(featureFlags, hasPermission, config.getIsSuperUser);
    },

    useCanQueries: (scopes: AppScopeIdentity[]) => {
      const hasPermission = permissionService.useHasPermission();
      const flagsByScope = featureService.useFeatureFlagsQueries(scopes);

      return flagsByScope.map(featureFlags =>
        bindCan(featureFlags, hasPermission, config.getIsSuperUser)
      );
    },

    can: async (scope: AppScopeIdentity, accessQuery: AccessQuery) => {
      const flags = await featureService.ensureFeatureFlags(scope);
      return checkAccess(
        accessQuery,
        permissionService.hasPermission,
        flags,
        config.getIsSuperUser
      );
    }
  };
}
